import type { AgentMessage, SdkMessage } from './types';
import { parseAgentMessage } from './protocol';
import { log } from './logger';

export type ConnectionState = 'connecting' | 'open' | 'closed';

const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 15_000;
const MAX_QUEUE = 50;

interface TransportHandlers {
  onMessage: (message: AgentMessage) => void;
  onOpen: () => void;
  onStateChange: (state: ConnectionState) => void;
}

export class Transport {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private queue: SdkMessage[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private closedByUs = false;
  private state: ConnectionState = 'closed';

  constructor(
    private readonly url: string,
    private readonly handlers: TransportHandlers,
  ) {}

  connect(): void {
    this.closedByUs = false;
    this.open();
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.handlers.onStateChange(state);
  }

  private open(): void {
    this.setState('connecting');
    let socket: WebSocket;
    try {
      socket = new WebSocket(this.url);
    } catch (error) {
      log.error('websocket could not be created', error);
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempt = 0;
      this.setState('open');
      log.info(`connected to ${this.url}`);
      this.handlers.onOpen();
      this.drain();
    });

    socket.addEventListener('message', (event) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(typeof event.data === 'string' ? event.data : '');
      } catch {
        log.warn('dropped a message that was not valid JSON');
        return;
      }
      const message = parseAgentMessage(parsed);
      if (!message) {
        log.warn('dropped a message that does not match the contract', parsed);
        return;
      }
      this.handlers.onMessage(message);
    });

    socket.addEventListener('close', () => {
      this.socket = null;
      this.setState('closed');
      if (!this.closedByUs) this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      log.warn('websocket error');
    });
  }

  private scheduleReconnect(): void {
    this.attempt += 1;
    const exponential = Math.min(BASE_BACKOFF_MS * 2 ** (this.attempt - 1), MAX_BACKOFF_MS);
    const delay = Math.round(exponential * (0.7 + Math.random() * 0.6));
    log.info(`reconnecting in ${delay}ms (attempt ${this.attempt})`);
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  send(message: SdkMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
      return;
    }
    if (this.queue.length < MAX_QUEUE) this.queue.push(message);
  }

  private drain(): void {
    const pending = this.queue;
    this.queue = [];
    for (const message of pending) this.send(message);
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  close(): void {
    this.closedByUs = true;
    clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
    this.queue = [];
    this.setState('closed');
  }
}
